import {
  AfterViewInit,
  Component,
  DestroyRef,
  Input,
  OnChanges,
  OnInit,
  SimpleChange,
  SimpleChanges,
  ViewChild,
  ViewContainerRef,
  inject,
  ChangeDetectionStrategy,
} from '@angular/core';
import { BehaviorSubject, Observable, Subject, take, tap } from 'rxjs';
import { DomSanitizer, SafeStyle } from '@angular/platform-browser';
import { Post, User } from 'src/app/core/models/core.models';
import { selectPostById, selectUser } from 'src/app/core/reducers';

import { AppFeatures } from 'src/app/core/app.features';
import { AppState } from 'src/app/reducers';
import { FormGroup } from '@angular/forms';
import { NomSentiment } from 'src/app/core/constants';
import { PostFullContentComponent } from '../../post/post-full-content/post-full-content.component';
import { PostImageTaggingComponent } from '../../post/post-image-tagging/post-image-tagging.component';
import { PostQuickStoryComponent } from '../../post/post-quick-story/post-quick-story.component';
import { PostScreenCaptureComponent } from '../../post/post-screen-capture/post-screen-capture.component';
import { PostSourceInfoComponent } from '../../post/post-source-info/post-source-info.component';
import { PostSynopsisComponent } from '../../post/post-synopsis/post-synopsis.component';
import { PostTranslationComponent } from '../../post/post-translation/post-translation.component';
import { PostVideoComponent } from '../../post/post-video/post-video.component';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { loadPost } from 'src/app/core/actions/post.actions';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { toggleFeedView } from 'src/app/core/actions/feed.actions';

/** Identifies the screen-capture tab so its visibility can be toggled by key. */
const SCREEN_CAPTURE_TAB_KEY = 'screen-capture';

@Component({
  selector: 'app-post-card-details',
  templateUrl: './post-card-details.component.html',
  styleUrls: ['./post-card-details.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class PostCardDetailsComponent
  implements OnInit, AfterViewInit, OnChanges
{
  @Input() postId: string;
  @Input() feedId: string;

  post$: Observable<Post>;
  post: Post;
  user: User;

  currentComponent = null;
  @ViewChild('tabContent', { read: ViewContainerRef })
  tabContent: ViewContainerRef;
  backgroundStyle: any;
  NomSentiment = NomSentiment;

  public searchString: string = '';
  public searchString$: BehaviorSubject<string>;
  public searchInputEventEmitter: Subject<string> = new Subject();

  tabs: Array<any>;

  private destroyRef = inject(DestroyRef);

  constructor(
    protected store: Store<AppState>,
    private translateService: TranslateService,
  ) {
    this.store
      .select(selectUser)
      .pipe(
        tap((user) => {
          this.user = user;
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  ngOnChanges(): void {
    // console.log('PostCardDetailsComponent ngOnChanges');

    if (this.postId) {
      this.store.dispatch(
        loadPost({ postId: this.postId, feedId: this.feedId }),
      );
      this.post$ = this.store.select(selectPostById(this.postId));
      this.post$
        .pipe(
          tap((post) => {
            this.post = post;
            // The post loads after setupTabs() runs, so the screen-capture tab's
            // visibility has to be re-evaluated once the post is in the store.
            this.updateScreenCaptureTabVisibility();
          }),
          takeUntilDestroyed(this.destroyRef),
        )
        .subscribe();
      this.updateTabsCachedComponentsInputs({ postId: this.postId });
    }
    this.setupTabs();
  }

  ngOnInit(): void {
    // this.store.dispatch(loadPost({ postId: this.postId }));
    // this.post$ = this.store.select(selectPostById(this.postId));

    // this.post$.pipe(take(1)).subscribe((post) => {
    //   this.post = post;
    // });

    this.setupTabs();
  }

  private setupTabs() {
    this.tabs = [
      {
        label: this.translateService.instant(
          'post-card-details.label.full-content',
        ),
        active: true,
        component: PostFullContentComponent,
        inputs: {
          postId: this.postId,
        },
        visible:
          this.user.feature_components[
            AppFeatures.POST_DETAILS_PANE_FULL_CONTENT
          ],
      },
      {
        label: this.translateService.instant('post-card-details.label.video'),
        component: PostVideoComponent,
        inputs: {
          postId: this.postId,
        },
        visible: true,
      },
      {
        label: this.translateService.instant(
          'post-card-details.label.screen-capture',
        ),
        component: PostScreenCaptureComponent,
        inputs: {
          postId: this.postId,
        },
        // Only meaningful when the post actually carries a capture; re-evaluated
        // in updateScreenCaptureTabVisibility() once the post resolves.
        visible: !!this.post?.post_screen_capture,
        key: SCREEN_CAPTURE_TAB_KEY,
      },
      {
        label: this.translateService.instant('post-card-details.label.source'),
        component: PostSourceInfoComponent,
        inputs: {
          postId: this.postId,
        },
        visible: false,
      },
      {
        label: this.translateService.instant(
          'post-card-details.label.synopsis',
        ),
        component: PostSynopsisComponent,
        inputs: {
          postId: this.postId,
        },
        visible:
          this.user.feature_components[AppFeatures.POST_DETAILS_PANE_SYNOPSIS],
      },

      {
        label: this.translateService.instant(
          'post-card-details.label.translation',
        ),
        component: PostTranslationComponent,
        inputs: {
          postId: this.postId,
        },
        visible:
          this.user.feature_components[
            AppFeatures.POST_DETAILS_PANE_TRANSLATION
          ],
      },

      {
        label: this.translateService.instant(
          'post-card-details.label.quick-story-report',
        ),
        component: PostQuickStoryComponent,
        inputs: {
          postId: this.postId,
        },
        visible:
          this.user.feature_components[
            AppFeatures.POST_DETAILS_PANE_QUICK_STORY_REPORT
          ],
      },

      {
        label: this.translateService.instant(
          'post-card-details.label.image-tagging',
        ),
        component: PostImageTaggingComponent,
        inputs: {
          postId: this.postId,
        },
        visible: Object.prototype.hasOwnProperty.call(
          this.user.feature_components,
          AppFeatures.FEED_SEARCH_RESULTS_ARTICLE_IMAGE_FACE_DETECTION,
        ),
      },
    ];

    this.tabs.forEach((tab, index) => {
      if (tab.active) {
        this.selectTab(tab, index);
      }
    });
  }

  /**
   * The screen-capture tab is shown only for posts that carry a
   * `post_screen_capture` URL. If the tab is hidden while active (e.g. after
   * switching to a post without a capture), fall back to the first visible tab
   * so the pane never renders an empty body.
   */
  private updateScreenCaptureTabVisibility(): void {
    const tab = this.tabs?.find((t) => t.key === SCREEN_CAPTURE_TAB_KEY);
    if (!tab) return;

    tab.visible = !!this.post?.post_screen_capture;

    if (!tab.visible && tab.active) {
      const fallbackIndex = this.tabs.findIndex((t) => t.visible);
      if (fallbackIndex > -1) {
        this.selectTab(this.tabs[fallbackIndex], fallbackIndex);
      }
    }
  }

  ngAfterViewInit(): void {
    if (this.tabs) {
      this.tabs.forEach((tab, index) => {
        if (tab.active) {
          this.selectTab(tab, index);
        }
      });
    }
  }

  loadComponent(component, inputs: any, index: number): void {
    const viewContainerRef = this.tabContent;
    const existingTab = this.tabs[index];

    // Detach all currently attached views
    viewContainerRef?.detach();

    if (!existingTab.componentRef) {
      // If the component has not been created yet, create and store it
      const componentRef = viewContainerRef?.createComponent(component);
      if (componentRef) {
        if (inputs) {
          for (let key of Object.keys(inputs)) {
            componentRef.instance[key] = inputs[key];
          }
        }
        existingTab.componentRef = componentRef;
      }
    } else {
      // Update inputs if needed
      const componentRef = existingTab.componentRef;
      if (inputs) {
        for (let key of Object.keys(inputs)) {
          componentRef.instance[key] = inputs[key];
        }
      }
    }

    // Reattach the view of the current component
    viewContainerRef?.insert(existingTab.componentRef.hostView);
    this.currentComponent = existingTab.componentRef;
  }

  updateTabsCachedComponentsInputs(inputs: any) {
    if (this.tabs) {
      this.tabs.forEach((tab) => {
        if (tab.componentRef) {
          const componentRef = tab.componentRef;
          const changes: SimpleChanges = {};

          for (let key of Object.keys(inputs)) {
            const currentValue = inputs[key];
            const previousValue = componentRef.instance[key];
            componentRef.instance[key] = currentValue;

            // Populate the changes object
            changes[key] = new SimpleChange(
              previousValue,
              currentValue,
              previousValue === undefined,
            );
          }

          // Manually trigger ngOnChanges
          if (
            Object.keys(changes).length > 0 &&
            componentRef.instance.ngOnChanges
          ) {
            componentRef.instance.ngOnChanges(changes);
          }
        }
      });
    }
  }

  updateCurrentComponentInputs(inputs: any): void {
    if (this.currentComponent) {
      const changes: SimpleChanges = {};

      for (let key of Object.keys(inputs)) {
        const currentValue = inputs[key];
        const previousValue = this.currentComponent.instance[key];
        this.currentComponent.instance[key] = currentValue;

        // Populate the changes object
        changes[key] = new SimpleChange(
          previousValue,
          currentValue,
          previousValue === undefined,
        );
      }

      // Manually trigger ngOnChanges
      if (
        Object.keys(changes).length > 0 &&
        this.currentComponent.instance.ngOnChanges
      ) {
        this.currentComponent.instance.ngOnChanges(changes);
      }
    }
  }

  public onSearchInput(event: KeyboardEvent): void {
    const inputValue = (event.target as HTMLInputElement).value;
    this.searchInputEventEmitter.next(inputValue);
  }

  onFormGroupChange(form: FormGroup, index: number) {}

  selectTab(tab, index): void {
    this.tabs.forEach((t) => (t.active = false));
    tab.active = true;
    this.loadComponent(tab.component, tab.inputs, index);
  }

  closeDetails() {
    this.store.dispatch(
      toggleFeedView({
        feedId: this.feedId,
        view: 'details',
        selected: false,
      }),
    );

    setTimeout(() => {
      window.dispatchEvent(new Event('resize'));
    }, 100);
  }
}
